import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AccessOptions } from '@shared/models/accessOptions.model';
import { InstanceIdService } from '@shared/services/instance-id.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { StorageService } from 'src/app/core/services/storage.service';
import { SwCacheService } from 'src/app/core/services/swCache.service';
import { UtilisService } from 'src/app/shared/services/utilis.service';
import { Profile } from '../../models/profile.model';
import { Skill } from '../../models/skill.model';
import { ErrorService } from '../../services/error.service';
import { MatchingresultsService } from '../../services/matchingresults.service';
import { ProfileService } from '../../services/profile.service';

@Component({
  selector: 'app-add-profile-dialog',
  templateUrl: './add-profile-dialog.component.html',
  styleUrls: ['./add-profile-dialog.component.scss'],
  standalone: false,
})
export class AddProfileDialogComponent implements OnInit {
  private instanceId: string;

  public isLoading: boolean = true;

  public step = 0;

  public skills: Skill[] | undefined;

  private profileOldVersion: any;

  public toastService: any;

  public topAccessOptions: AccessOptions | undefined;

  public showSkillsPopup: boolean = true;

  constructor(
    private profileService: ProfileService,
    private UtilisService: UtilisService,
    public dialogRef: MatDialogRef<AddProfileDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private errorService: ErrorService,
    private swCacheService: SwCacheService,
    private readonly matchingResultsService: MatchingresultsService,
    private storageService: StorageService,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
    if (this.data.topAccessOptions) {
      this.topAccessOptions = this.data.topAccessOptions;
    }
  }

  ngOnInit() {
    if (this.data && this.data.profile) {
      this.profileOldVersion = JSON.parse(JSON.stringify(this.data.profile));
    }
    if (this.data.onlySkills) {
      this.step = 1;
    }
  }

  profileChange(data: { profile: Profile; showSkillsPopup: boolean }): void {
    const { profile, showSkillsPopup } = data;
    this.swCacheService.deleteApiCache('api-stable-routes', [
      '**/api/profiles/by/Infirmiere',
      '**/api/profiles/by/Anesthésiste',
      '**/api/profiles/by/Anesth%C3%A9siste',
      '**/api/profiles/by/anesth',
      '**/api/profiles/by/Iade',
      '**/api/profiles/?',
      '**/api/profiles/allProfiles',
      '**/api/users/counter',
    ]);

    this.data.profile = JSON.parse(JSON.stringify(profile));

    if (showSkillsPopup) {
      this.step = 1;
    } else {
      this.step = 2;
      this.isLoading = true;
      if (this.data.openType === 'EDIT') {
        this.editProfile();
      } else {
        if (this.data.profile.matchingResultsToAdd || this.data.profile.matchingResultsToRemove) {
          this.subscriptionManager
            .createRequest(
              this.profileService.createProfileWithSkills({
                profile: this.data.profile,
              }),
              'createProfileWithSkills',
              this.instanceId
            )
            .pipe(
              switchMap((createdProfile: any) => {
                const observables = [];
                if (this.data.profile.matchingResultsToAdd) {
                  for (const matchingResultId of this.data.profile.matchingResultsToAdd) {
                    observables.push(
                      this.matchingResultsService.updateMatchingResults(matchingResultId, createdProfile._id ?? '')
                    );
                  }
                }
                if (this.data.profile.matchingResultsToRemove) {
                  for (const matchingResultId of this.data.profile.matchingResultsToRemove) {
                    observables.push(this.matchingResultsService.updateMatchingResults(matchingResultId, 'null'));
                  }
                }
                return forkJoin(observables).pipe(
                  map(() => {
                    return createdProfile;
                  })
                );
              })
            )
            .subscribe({
              next: (result: any) => {
                this.UtilisService.displaySuccessToast();
                this.dialogRef.close(result);
              },
              error: (error: any) => this.errorService.handleError(error),
            });
        } else {
          this.subscriptionManager
            .createRequest(
              this.profileService.createProfileWithSkills({ profile: this.data.profile }),
              'createProfileWithSkills',
              this.instanceId
            )
            .subscribe({
              next: (result: any) => {
                this.UtilisService.displaySuccessToast();
                this.dialogRef.close(result);
              },
              error: (error: any) => this.errorService.handleError(error),
            });
        }
      }
    }
  }

  editSkillsClicked(profile: any): void {
    this.data.profile = profile;
    this.step = 1;
  }

  editProfile(): void {
    if (this.data.profile.matchingResultsToAdd || this.data.profile.matchingResultsToRemove) {
      this.subscriptionManager
        .createRequest(this.profileService.editProfile(this.data.profile), 'editProfile', this.instanceId)
        .pipe(
          switchMap((updatedProfile: any) => {
            const observables = [];
            if (this.data.profile.matchingResultsToAdd) {
              for (const matchingResultId of this.data.profile.matchingResultsToAdd) {
                observables.push(
                  this.matchingResultsService.updateMatchingResults(matchingResultId, updatedProfile._id ?? '')
                );
              }
            }
            if (this.data.profile.matchingResultsToRemove) {
              for (const matchingResultId of this.data.profile.matchingResultsToRemove) {
                observables.push(this.matchingResultsService.updateMatchingResults(matchingResultId, 'null'));
              }
            }
            return forkJoin(observables).pipe(
              map(() => {
                return updatedProfile;
              })
            );
          })
        )
        .subscribe({
          next: (result: any) => {
            this.UtilisService.displaySuccessToast();
            this.dialogRef.close(result);
          },
          error: (error: any) => this.errorService.handleError(error),
        });
    } else {
      this.subscriptionManager
        .createRequest(this.profileService.editProfile(this.data.profile), 'editProfile', this.instanceId)
        .subscribe({
          next: (result: any) => {
            this.UtilisService.displaySuccessToast();
            this.dialogRef.close(result);
          },
          error: (error: any) => this.errorService.handleError(error),
        });
    }
  }

  /**
   * Given a skill for a role and a profile in the home page's level up request,
   * this function deletes this skill if the manager modifies
   * it from the individual skill's page.
   * @param skills the skills modified in the individual skill's page
   */
  /*removeLevelUpRequests(skills: any) {
    if(this.levelUpRequestsSubscription){
      this.levelUpRequestsSubscription.unsubscribe();
    }

    this.levelUpRequestsSubscription = this.levelUpRequestService
          .getLevelUpRequests()
          .subscribe(
            (levelUpRequests: LevelUpRequest[]) => {
              if(levelUpRequests) {
                levelUpRequests.forEach((levelUpRequest) => {
                  if(levelUpRequest.profile._id === this.data.profile._id) {
                    let levelUpRequestSkill = skills.find((skill: any) => skill.specialtyId === levelUpRequest.specialty._id);
                    let levelUpRequestRole = levelUpRequestSkill.ratings.find((rating: any) => rating.role._id === levelUpRequest.role._id);
                    // only delete the level up request if a change has be done
                    if(levelUpRequestRole && levelUpRequestRole.oldNurseLevel !== levelUpRequestRole.nurseLevel) {
                      this.levelUpRequestService.deleteLevelUpRequest(levelUpRequest._id)
                              .subscribe(
                                () => this.toastService.infoToast('Operation prise en charge'),
                                (error) => this.errorService.handleError(error)
                              );
                    }

                  }
                })

              }
            }
          )
  }*/

  SkillsValidation(skills: any) {
    if (this.data.openType === 'EDIT') {
      this.generateSkillsBody(skills);
      this.step = 2;
      this.isLoading = true;
      this.editProfileWithSkills();
    } else {
      this.generateSkillsBody(skills);
      this.step = 2;
      this.isLoading = true;
      this.createProfileWithSkills();
    }
  }

  reverse(s: string): string {
    return s.split('-').reverse().join('-');
  }

  editProfileWithSkills() {
    if (this.data.profile.matchingResultsToAdd || this.data.profile.matchingResultsToRemove) {
      this.subscriptionManager
        .createRequest(
          this.profileService.editProfileWithSkills({
            profile: this.data.profile,
            user: this.data.user,
            skills: this.skills,
          }),
          'editProfileWithSkills',
          this.instanceId
        )
        .pipe(
          switchMap((updatedProfile: any) => {
            const observables = [];
            if (this.data.profile.matchingResultsToAdd) {
              for (const matchingResultId of this.data.profile.matchingResultsToAdd) {
                observables.push(
                  this.matchingResultsService.updateMatchingResults(matchingResultId, updatedProfile._id ?? '')
                );
              }
            }
            if (this.data.profile.matchingResultsToRemove) {
              for (const matchingResultId of this.data.profile.matchingResultsToRemove) {
                observables.push(this.matchingResultsService.updateMatchingResults(matchingResultId, 'null'));
              }
            }
            return forkJoin(observables).pipe(
              map(() => {
                return updatedProfile;
              })
            );
          })
        )
        .subscribe({
          next: (result: any) => {
            this.UtilisService.displaySuccessToast();
            this.dialogRef.close(result);
          },
          error: (error: any) => this.errorService.handleError(error),
        });
    } else {
      this.subscriptionManager
        .createRequest(
          this.profileService.editProfileWithSkills({
            profile: this.data.profile,
            user: this.data.user,
            skills: this.skills,
          }),
          'editProfileWithSkills',
          this.instanceId
        )
        .subscribe({
          next: (result: any) => {
            this.UtilisService.displaySuccessToast();
            this.dialogRef.close(result);
          },
          error: (error: any) => this.errorService.handleError(error),
        });
    }
  }

  createProfileWithSkills() {
    if (this.data.profile.matchingResultsToAdd || this.data.profile.matchingResultsToRemove) {
      this.subscriptionManager
        .createRequest(
          this.profileService.createProfileWithSkills({
            profile: this.data.profile,
            skills: this.skills,
          }),
          'createProfileWithSkills',
          this.instanceId
        )
        .pipe(
          switchMap((createdProfile: any) => {
            const observables = [];
            if (this.data.profile.matchingResultsToAdd) {
              for (const matchingResultId of this.data.profile.matchingResultsToAdd) {
                observables.push(
                  this.matchingResultsService.updateMatchingResults(matchingResultId, createdProfile._id ?? '')
                );
              }
            }
            if (this.data.profile.matchingResultsToRemove) {
              for (const matchingResultId of this.data.profile.matchingResultsToRemove) {
                observables.push(this.matchingResultsService.updateMatchingResults(matchingResultId, 'null'));
              }
            }
            return forkJoin(observables).pipe(
              map(() => {
                return createdProfile;
              })
            );
          })
        )
        .subscribe({
          next: (result: any) => {
            this.UtilisService.displaySuccessToast();
            this.dialogRef.close(result);
          },
          error: (error: any) => this.errorService.handleError(error),
        });
    } else {
      this.subscriptionManager
        .createRequest(
          this.profileService.createProfileWithSkills({
            profile: this.data.profile,
            skills: this.skills,
          }),
          'createProfileWithSkills',
          this.instanceId
        )
        .subscribe({
          next: (result: any) => {
            this.UtilisService.displaySuccessToast();
            this.dialogRef.close(result);
          },
          error: (error: any) => this.errorService.handleError(error),
        });
    }
  }

  generateSkillsBody(unstructuredSkills: any[]) {
    const skills: Skill[] = [];
    for (const unstructuredSkill of unstructuredSkills) {
      for (const rating of unstructuredSkill.ratings) {
        const skill: any = {};
        skill.specialty = unstructuredSkill.specialtyId;
        skill.role = rating.role._id;
        skill.level = rating.nurseLevel;
        skill.timeIndicator = rating.timeIndicator;
        skill.oldNurseLevel = rating.oldNurseLevel;
        skills.push(skill);
      }
    }
    this.skills = skills;
  }

  close(): void {
    if (this.profileOldVersion) {
      this.data.profile = JSON.parse(JSON.stringify(this.profileOldVersion));
    }
    if (this.data && this.data.profile && this.data.profile._id === this.storageService.getUser()?.profile?._id) {
      this.storageService.profilePictureInLocalStorageSubject.next(this.data.profile.profilePic);
    }
    this.dialogRef.close();
  }
}
